<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search Interest Type..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
            <button type="button" :disabled="loading" class="theme-button px-5"
              @click="resetForm(); isEditing = false; editingId = null; riskConfigDialog = true">
              <span v-if="!loading"><v-icon start>mdi-content-save-cog-outline</v-icon> Save Risk Category </span>
            </button>
          </div>

          <!-- PrimeVue DataTable with search ---NB: add showGridlines to show column lines-->
          <DataTable v-model:filters="filters" :value="riskCategory" paginator :rows="10" dataKey="uuid" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" selectionMode="single" :globalFilterFields="['name']">

            <!-- Empty table -->
            <template #empty>
              No risk category found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading products...
            </template>

            <!-- Product Column -->
            <Column header="Risk Level">
              <template #body="slotProps">
                <div class="product-cell">
                  <!-- <img
                    :src="slotProps.data.icon ? `${STORAGE_URL}/${slotProps.data.icon}` : defaultImage"
                    class="product-image" /> -->
                  <div>

                    <Tag :style="{
                      border: `2px solid ${slotProps.data.colour_code}`,
                      background: 'transparent',
                      color: slotProps.data.colour_code
                    }" rounded>
                      <div class="flex items-center gap-2 px-1">
                        <span class="text-base">
                          <!-- {{ slotProps.data.account_status === 'active' ? 'Active' : 'Inactive' }} -->
                          <div class="product-name">{{ slotProps.data.name }}</div>
                        </span>
                      </div>
                    </Tag>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column header="Added On">
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.created_at) }}
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="" style="width:120px; border-radius: 10px;">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEditModal(slotProps.data)">
                      Edit {{ slotProps.data.name }} Details
                    </v-list-item>
                  </v-list>

                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <v-dialog v-model="riskConfigDialog" max-width="650">
          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">
              {{ isEditing ? 'Edit Risk Category' : 'Create Risk Category' }}
            </v-card-title>

            <form @submit.prevent="handleCreateRisk" class="space-y-5">


              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Name</label>
                <v-text-field v-model="form.name" type="text" variant="outlined"
                  placeholder="Risk Name e.g High" />
              </div>

              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Color Code</label>

                <v-text-field v-model="form.colour_code" variant="outlined" placeholder="#0f9d58" />

                <!-- optional visual picker -->
                <v-color-picker v-model="form.colour_code" hide-inputs mode="hex" show-swatches class="mt-2" />
              </div>

              <div class="flex flex-col gap-2 mt-4">
                <label class="text-sm font-medium mb-4">Risk Image</label>

                <v-file-input v-model="form.icon" variant="outlined" accept="image/*" label="Upload image"
                  prepend-icon="mdi-camera" />
              </div>


              <!-- ACTIONS -->
              <div class="flex justify-center align-center  gap-3 pt-4"
                style="display:flex; justify-content:center; align-items:center; gap:12px; padding-top:16px;">
                <v-btn variant="text" @click="riskConfigDialog = false">
                  Cancel
                </v-btn>
                <v-btn color="black" type="submit" :loading="riskLoading" :disabled="!isFormValid">
                  {{ isEditing ? 'Update Risk' : 'Save Risk' }}
                </v-btn>
              </div>

            </form>
          </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
          snackbar.message }}
          <template #actions>
            <v-btn variant="outlined" @click="snackbar.show = false">
              Cancel
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { useInvStore } from "@/store/investment"
import { formatDateTime, getAllErrors } from "@/util"
import { executeRisk, updateRisk } from "@/api/investment"

//const auth = useAuthStore()
const investRisk = useInvStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })
const riskLoading = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const riskConfigDialog = ref(false)
//const defaultImage = avatarImage

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}

/* Risk */
const riskCategory = computed(() => investRisk.riskCategory || [])

const form = ref({
  name: "",
  colour_code: "#000000",
  icon: null
})

/**
 * FORM VALIDATION STATE
 */
const isFormValid = computed(() => {
  return (
    form.value.name &&
    form.value.colour_code &&
    form.value.icon
  )
})

/**
 * RESET FORM
 */
const resetForm = () => {
  form.value = {
    name: "",
    colour_code: null,
    icon: "",
  }
}

//Function to create or edit
const handleCreateRisk = async () => {
  riskLoading.value = true

  try {
   const payload = new FormData()

    payload.append("name", form.value.name)
    payload.append("colour_code", form.value.colour_code)
    payload.append("icon", form.value.icon)

    console.log(form.value.icon)

    //await executeFeeConfig(payload)
    if (isEditing.value) {
      // update api
      await updateRisk(editingId.value, payload)
    } else {
      // create api
      await executeRisk(payload)
    }

    // reload updated data
    await investRisk.loadRiskCategory()

    //close the modal
    //feeConfigDialog.value = false
    showSnackbar("Risk saved successfully")

    //resetForm()
    //feeConfigDialog.value = false

  } catch (e) {


    const msg = getAllErrors(e)
    showSnackbar(
      msg || "Failed to save risk",
      "error"
    )
  } finally {
    riskLoading.value = false
  }
}

//editing modal
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.uuid

  form.value.name = item.name || ""
  form.value.colour_code = item.colour_code ?? ""
  form.value.icon = item.icon || ""

  riskConfigDialog.value = true
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  
  try {
    await investRisk.loadRiskCategory()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}


// Edit Modal
// const editDialog = ref(false)
// const selectedProduct = ref({})

// const openEditModal = (product) => {
//   selectedProduct.value = { ...product } // copy to avoid mutating table directly
//   editDialog.value = true
// }

// const closeEditModal = () => editDialog.value = false
// const saveEdit = () => {
//   console.log("Save", selectedProduct.value)
//   editDialog.value = false
// }
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 5px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>
